import React, { useState } from "react";
import { Card, Typography, Space, Tag, Divider, Alert, Descriptions, Button, List, Collapse, Badge, Tooltip, Row, Col, Table, Statistic } from "antd";
import { CheckOutlined, EditOutlined, InfoCircleOutlined, DollarOutlined, FileTextOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined, DatabaseOutlined } from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * 结算账单审阅组件
 * V5.0 更新：标准化处理器接口
 * 专门用于显示和处理结算代理生成的账单创建操作
 * @param {Object} props.actionItem - Action Item 数据
 * @param {Function} props.onConfirm - 确认回调
 * @param {Function} props.onReject - 拒绝回调
 * @param {boolean} props.loading - 是否正在处理
 * @param {boolean} props.readOnly - 是否只读模式
 */
const SettlementInvoiceReview = ({ actionItem, onConfirm, onReject, loading = false, readOnly = false }) => {
    const [expandedSections, setExpandedSections] = useState(["0"]); // 默认展开第一个section

    const { suggested_payload } = actionItem;
    const { system_operations = [], reasoning } = suggested_payload;

    // 获取账单创建操作
    const invoiceOperation = system_operations.find((op) => op.operation_type === "CREATE_INVOICE_RECORDS");
    const invoiceData = invoiceOperation?.operation_parameters?.invoice_data;

    if (!invoiceData) {
        return <Alert message="数据格式错误" description="未找到账单数据，请检查数据格式" type="error" showIcon />;
    }

    // 渲染航班匹配信息
    const renderFlightMatch = (item) => {
        const getMatchColor = (confidence) => {
            switch (confidence) {
                case "HIGH":
                    return "success";
                case "MEDIUM":
                    return "warning";
                case "LOW":
                    return "error";
                default:
                    return "default";
            }
        };

        return (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Space>
                    <Tag color={getMatchColor(item.match_confidence)}>{item.match_confidence} 置信度</Tag>
                    {item.matched_flight_id && <Tag color="blue">航班ID: {item.matched_flight_id}</Tag>}
                </Space>
                {item.match_reasoning && (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        {item.match_reasoning}
                    </Text>
                )}
            </Space>
        );
    };

    // 渲染异常信息
    const renderAnomalies = (anomalies) => {
        if (!anomalies || anomalies.length === 0) return null;

        return (
            <Alert
                message="检测到异常"
                description={
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {anomalies.map((anomaly, index) => (
                            <li key={index}>{anomaly}</li>
                        ))}
                    </ul>
                }
                type="warning"
                size="small"
                showIcon
            />
        );
    };

    // 渲染行项目表格
    const renderLineItemsTable = (lineItems) => {
        const columns = [
            {
                title: "ID",
                dataIndex: "item_id",
                key: "item_id",
                width: 80,
            },
            {
                title: "项目",
                dataIndex: "description",
                key: "description",
                width: 120,
            },
            {
                title: "数量",
                dataIndex: "quantity",
                key: "quantity",
                width: 80,
                align: "right",
                render: (value) => value?.toLocaleString(),
            },
            {
                title: "单价",
                dataIndex: "unit_price",
                key: "unit_price",
                width: 80,
                align: "right",
                render: (value) => `$${value?.toFixed(4)}`,
            },
            {
                title: "总价",
                dataIndex: "total_price",
                key: "total_price",
                width: 100,
                align: "right",
                render: (value) => `$${value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
            },
            {
                title: "分配类型",
                dataIndex: "allocation_type",
                key: "allocation_type",
                width: 80,
                render: (value) => <Tag color={value === "DIRECT" ? "green" : "orange"}>{value === "DIRECT" ? "直接" : "间接"}</Tag>,
            },
            {
                title: "航班匹配",
                key: "flight_match",
                width: 200,
                render: (_, record) => renderFlightMatch(record),
            },
            {
                title: "异常",
                dataIndex: "anomalies",
                key: "anomalies",
                width: 150,
                render: (anomalies) => renderAnomalies(anomalies),
            },
            {
                title: "附加信息",
                dataIndex: "additional_info",
                key: "additional_info",
                width: 250,
                render: (info) => {
                    if (!info || Object.keys(info).length === 0) return "无";
                    return (
                        <Collapse size="small" bordered={false}>
                            <Panel header="查看详情" key="1" style={{ fontSize: "12px" }}>
                                <Descriptions column={1} size="small" bordered>
                                    {Object.entries(info).map(([key, value]) => (
                                        <Descriptions.Item label={key} key={key}>
                                            {String(value)}
                                        </Descriptions.Item>
                                    ))}
                                </Descriptions>
                            </Panel>
                        </Collapse>
                    );
                },
            },
        ];

        return <Table columns={columns} dataSource={lineItems.map((item, index) => ({ ...item, key: index }))} pagination={false} size="small" scroll={{ x: 800 }} />;
    };

    // 渲染账单概览
    const renderInvoiceOverview = () => (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
                <Col span={6}>
                    <Statistic title="账单号" value={invoiceData.invoice_number} prefix={<FileTextOutlined />} />
                </Col>
                <Col span={6}>
                    <Statistic title="账单日期" value={invoiceData.invoice_date} prefix={<InfoCircleOutlined />} />
                </Col>
                <Col span={6}>
                    <Statistic title="币种" value={invoiceData.currency} prefix={<DollarOutlined />} />
                </Col>
                <Col span={6}>
                    <Statistic title="总金额" value={invoiceData.grand_total} precision={2} prefix={<DollarOutlined />} valueStyle={{ color: "#1890ff" }} />
                </Col>
            </Row>
            {invoiceData.remarks && (
                <div style={{ marginTop: 16 }}>
                    <Text strong>备注：</Text>
                    <Text>{invoiceData.remarks}</Text>
                </div>
            )}
        </Card>
    );

    // 渲染账单分节
    const renderInvoiceSections = () => {
        if (!invoiceData.sections || invoiceData.sections.length === 0) {
            return <Alert message="未找到账单分节数据" type="info" />;
        }

        return (
            <Collapse activeKey={expandedSections} onChange={setExpandedSections} size="small">
                {invoiceData.sections.map((section, index) => (
                    <Panel
                        header={
                            <Space>
                                <Text strong>{section.section_identifier}</Text>
                                <Tag color="blue">{section.line_items?.length || 0} 项目</Tag>
                                <Tag color="green">小计: ${section.sub_total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Tag>
                            </Space>
                        }
                        key={index.toString()}
                    >
                        {section.line_items && section.line_items.length > 0 ? renderLineItemsTable(section.line_items) : <Alert message="该分节无行项目数据" type="info" size="small" />}
                    </Panel>
                ))}
            </Collapse>
        );
    };

    return (
        <div>
            {/* AI 分析推理 */}
            {reasoning && (
                <Card size="small" style={{ marginBottom: 16 }}>
                    <Title level={5}>
                        <InfoCircleOutlined /> AI 分析结果
                    </Title>
                    <Paragraph style={{ whiteSpace: "pre-line", fontSize: "14px" }}>{reasoning}</Paragraph>
                </Card>
            )}

            {/* 账单概览 */}
            <Title level={5}>
                <FileTextOutlined /> 账单概览
            </Title>
            {renderInvoiceOverview()}

            {/* 账单分节详情 */}
            <Title level={5}>
                <DatabaseOutlined /> 账单明细
            </Title>
            {renderInvoiceSections()}

            <Divider />

            {/* 操作按钮 */}
            <Space>
                <Button type="primary" icon={<CheckOutlined />} onClick={() => onConfirm(actionItem)} loading={loading} size="large">
                    确认并创建账单记录
                </Button>
                <Button icon={<CloseCircleOutlined />} onClick={() => onReject(actionItem)} loading={loading} size="large">
                    拒绝
                </Button>
            </Space>
        </div>
    );
};

export default SettlementInvoiceReview;
